import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Autocomplete, } from '@awaze/design-system.components.autocomplete';
import { Button } from '@awaze/design-system.components.button';
import { Drawer, DrawerContent } from '@awaze/design-system.components.drawer';
import { PinMap } from '@awaze/design-system.components.icon';
import { InputFacade } from '@awaze/design-system.components.input-facade';
import { Stack } from '@awaze/design-system.components.stack';
import { Prose, Text } from '@awaze/design-system.components.text';
import { dynamicLabel, isPopularDestination, } from '@awaze/design-system.utils.form';
import { breakpoints, pxToRems, useDisclosure, } from '@awaze/design-system.utils.react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DestinationList } from './destination-list';
import { DestinationAccordion } from './destination-selector-legacy/destination-accordion';
import { DestinationTabList } from './destination-selector-legacy/destination-tab-list';
import { MobileBox, onDesktop, onMobile, useHandleFormatChange, } from './search-form-composition.styles';
import { useSearchFormContext } from '../search-form.context';
import { getAutocompleteSource } from './destination-list.utils';
const PinMapAdjust = styled(PinMap) `
  & > div {
    height: 100% !important;
  }
`;
const StyledNoResults = styled(Prose) `
  padding-top: ${pxToRems(8)};

  ${onDesktop} {
    padding-top: 0;
  }
`;
const StyledWideAutocompleteContainer = styled.div `
  display: flex;
  &:focus-within {
    z-index: 1;
  }

  flex: 2 !important;

  ${onMobile} {
    display: none;
  }

  > span {
    height: 100%;
    display: flex;
    width: 100%;

    > div {
      height: 100%;
      display: flex;
      align-items: center;
      width: 100%;

      &:first-child {
        width: 100%;
      }
    }
  }

  input {
    border: 2px solid transparent;
    margin: 0;

    &:hover {
      border-color: transparent;
    }

    &:active,
    &:focus {
      outline: 2px solid
        ${({ theme }) => theme.ColorComponentSearchBarBorderActive};
      box-shadow: none;
      border-color: transparent;
    }
  }
`;
const StyledTextLegacyAccordionHeader = styled(Text) `
  margin-top: ${pxToRems(14)};
`;
export const StyledDrawerBody = styled.div `
  margin: 0 ${pxToRems(24)} ${pxToRems(24)};
`;
export const DestinationSelector = ({ translations, autoSuggestSearch, boundaryElement = null, destinationListConfig, onDestinationSelected, }) => {
    const { formHooks, useLegacyDestinationSelector } = useSearchFormContext();
    const [currentSearchTerm, setCurrentSearchTerm] = useState('');
    const [persistPopover, setPersistPopover] = useState(true);
    const [noResult, setNoResult] = useState(false);
    const [isSearchCallback, setIsSearchCallback] = useState(false);
    useEffect(() => {
        setCurrentSearchTerm(formHooks.getValues('destination'));
        setNoResult(false);
    }, [formHooks.watch('destination')]);
    const { isOpen: isDestinationOpen, open: openDestination, close: closeDestination, } = useDisclosure();
    useEffect(() => {
        !isSearchCallback &&
            currentSearchTerm !== formHooks.getValues('destination') &&
            setCurrentSearchTerm(formHooks.getValues('destination'));
    }, [isSearchCallback]);
    const handleOnChange = (input) => {
        setIsSearchCallback(!!input);
        setCurrentSearchTerm(input);
    };
    const searchHandlerCallback = (searchTerm) => {
        if (!searchTerm) {
            return null;
        }
        const [accommodationType] = formHooks.getValues(['accommodationType']);
        return autoSuggestSearch(searchTerm, accommodationType);
    };
    const noResultBox = ({ onClick, }) => (_jsxs(StyledNoResults, { children: [_jsx(Text, { sizes: 300, children: dynamicLabel(translations.autocomplete.noResult, `‘${currentSearchTerm}’`) }), _jsx(Button, { onClick: onClick, colourScheme: "brand-primary", variant: "outline", text: translations.autocomplete.noResultBtn, isFullWidth: !window?.matchMedia(`(min-width: ${breakpoints[0]}px)`)?.matches })] }));
    const autocompleteLabels = {
        ...translations.autocomplete,
        noResult: noResultBox,
    };
    const onItemClickCallback = (item) => {
        const source = getAutocompleteSource(item);
        if (item.link) {
            // location-lookup
            if (isPopularDestination(item.id)) {
                formHooks.setValue('featureId', item.id);
                formHooks.setValue('destinationId', '');
            }
            else {
                formHooks.setValue('destinationId', item.id);
                formHooks.setValue('featureId', '');
            }
        }
        else {
            // places-api
            if (item.legacyId && isPopularDestination(item.legacyId)) {
                formHooks.setValue('featureId', item.legacyId);
                formHooks.setValue('destinationId', '');
            }
            else {
                formHooks.setValue('featureId', '');
                formHooks.setValue('destinationId', item.drupalId || item.legacyId);
            }
        }
        formHooks.setValue('id', item.id);
        formHooks.setValue('destination', item.name);
        formHooks.setValue('destinationURL', item.link);
        setIsSearchCallback(true);
        setNoResult(false);
        closeDestination();
        onDestinationSelected('autocomplete', source);
    };
    const onInputClearCallback = () => {
        formHooks.setValue('id', '');
        formHooks.setValue('destination', '');
        formHooks.setValue('destinationId', '');
        formHooks.setValue('featureId', '');
        formHooks.setValue('destinationURL', '');
        setIsSearchCallback(false);
        setNoResult(false);
    };
    const onClearFacade = () => {
        formHooks.setValue('id', '');
        formHooks.setValue('destination', '');
        formHooks.setValue('destinationId', '');
        formHooks.setValue('featureId', '');
        formHooks.setValue('destinationURL', '');
        setIsSearchCallback(false);
        setNoResult(false);
        openDestination();
    };
    const isBoatsDestinations = formHooks.watch('accommodationType') === 'boat-holidays';
    const accommodationPrevious = (value) => {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    };
    const prevAccommodation = accommodationPrevious(formHooks.getValues('accommodationType'));
    useEffect(() => {
        setPersistPopover(!useLegacyDestinationSelector);
    }, [useLegacyDestinationSelector]);
    useEffect(() => {
        if (prevAccommodation &&
            prevAccommodation !== formHooks.getValues('accommodationType')) {
            setCurrentSearchTerm('');
            setIsSearchCallback(false);
        }
    }, [formHooks.watch('accommodationType')]);
    const popoverClosedCallback = (isPopoverOpen) => {
        setPersistPopover(!useLegacyDestinationSelector);
        if (!isPopoverOpen) {
            setIsSearchCallback(false);
            setNoResult(false);
            // Set formHooks destination on popover closing
            setCurrentSearchTerm(formHooks.getValues('destination'));
        }
    };
    const closeAutocompletePopover = () => {
        setPersistPopover(false);
    };
    const popoverWidth = isSearchCallback ? pxToRems(650) : pxToRems(360);
    useHandleFormatChange(closeDestination);
    return (_jsxs(_Fragment, { children: [_jsx(StyledWideAutocompleteContainer, { "data-ga": "destination-selector", children: _jsx(Autocomplete, { placement: noResult || isSearchCallback ? 'bottom-start' : 'bottom', icon: _jsx(PinMapAdjust, {}), offset: { left: 0, top: 16 }, value: currentSearchTerm, translations: autocompleteLabels, resultsHandlerCallback: searchHandlerCallback, handleOnChange: handleOnChange, onItemClickCallback: onItemClickCallback, onInputClearCallback: onInputClearCallback, popoverWidth: (isSearchCallback || noResult) && currentSearchTerm?.length >= 3
                        ? popoverWidth
                        : '100%', openOnInputFocus: true, highlightOnFocus: true, boundaryElement: boundaryElement, closingPopoverCallback: popoverClosedCallback, persistPopoverOnNavigation: useLegacyDestinationSelector ? false : persistPopover, setNoResult: setNoResult, useLegacyDestinationSelector: useLegacyDestinationSelector, isInputTouched: isSearchCallback, children: useLegacyDestinationSelector ? (_jsx(DestinationTabList, { onDestinationSelected: onDestinationSelected, translations: translations, popularMaximumColumnItems: 16, isBoatsDestinations: isBoatsDestinations })) : (_jsx(DestinationList, { ...destinationListConfig, translations: translations, closeDestinationsList: closeAutocompletePopover, onDestinationSelected: onDestinationSelected, isSearchCallback: setIsSearchCallback })) }) }), _jsxs(MobileBox, { margin: [4, 4, 4], radius: 4, colourScheme: "white", padding: [0, 0, 0], shadow: 2, children: [_jsx(InputFacade, { onClick: openDestination, label: "destination", icon: _jsx(PinMap, {}), iconLabel: translations.iconLabel, placeholder: translations.placeholder, value: currentSearchTerm, onClear: onClearFacade, isFullWidth: true, hideLabel: true, clearButtonAriaLabel: translations.inputFacadeClearButtonAriaLabel }), _jsx(Drawer, { isOpen: isDestinationOpen, placement: "right", close: closeDestination, children: _jsx(DrawerContent, { header: translations.searchFormTitle, children: _jsx(StyledDrawerBody, { children: _jsxs(Stack, { spacing: 0, children: [_jsx(Text, { sizes: 300, type: "mid", children: translations.inputTitle }), _jsxs(Autocomplete, { icon: _jsx(PinMapAdjust, {}), value: currentSearchTerm, translations: autocompleteLabels, handleOnChange: handleOnChange, resultsHandlerCallback: searchHandlerCallback, onItemClickCallback: onItemClickCallback, onInputClearCallback: onInputClearCallback, inlineResults: true, highLightAndFocusOnInit: true, highlightOnFocus: true, closingPopoverCallback: popoverClosedCallback, persistPopoverOnNavigation: useLegacyDestinationSelector ? false : persistPopover, isInputTouched: isSearchCallback, children: [useLegacyDestinationSelector && (_jsx(StyledTextLegacyAccordionHeader, { sizes: 300, type: "mid", mb: 16, children: translations.accordionTitle })), _jsx(_Fragment, { children: useLegacyDestinationSelector ? (_jsx(DestinationAccordion, { closeAccordion: closeDestination, translations: translations, isBoatsDestinations: isBoatsDestinations, useLegacyDestinationSelector: useLegacyDestinationSelector })) : (_jsx(DestinationList, { ...destinationListConfig, translations: translations, closeDestinationsList: closeDestination, onDestinationSelected: onDestinationSelected, isSearchCallback: setIsSearchCallback })) })] })] }) }) }) })] })] }));
};
//# sourceMappingURL=destination-selector.js.map